// crewbit-admin/src/components/schedule/DayStaffColumns.tsx
//
// Schedule Module Phase C C1.5 — staff-columns layout (admin variant).
//
// Admin renders parallel <ResourceColumn> instances side-by-side (one per
// operator). Drag-drop reassign across columns is the V-4 critical path
// (consumers wire useReassignScheduleEntry into onResourceDrop in C1.6).
//
// NO industry-keyed branching per §0.10 — operator list comes from shifts upstream.

import type { ScheduleEntry } from '../../types/schedule';
import { admin } from '../../styles/tokens';
import { ResourceColumn, type ResourceColumnOperator } from './ResourceColumn';

export interface DayStaffColumnsProps {
  operators: ResourceColumnOperator[];
  entries: ScheduleEntry[];
  travelBufferMinutes: number;
  onEntryTap: (entryId: string) => void;
  onEntryLongPress?: (entryId: string) => void;
  /** Drag-drop cross-column reassign handler — wired to useReassignScheduleEntry in C1.6. */
  onResourceDrop?: (droppedEntryId: string, newResourceId: string) => void;
  showTravelSlivers?: boolean;
  industryAccent?: string;
}

function groupByOperator(
  entries: ScheduleEntry[],
  operators: ResourceColumnOperator[],
): Record<string, ScheduleEntry[]> {
  const map: Record<string, ScheduleEntry[]> = {};
  for (const op of operators) map[op.id] = [];
  for (const e of entries) {
    if (!Array.isArray(e.assigned_to)) continue;
    for (const opId of e.assigned_to) {
      if (map[opId]) map[opId].push(e);
    }
  }
  return map;
}

export function DayStaffColumns({
  operators,
  entries,
  travelBufferMinutes,
  onEntryTap,
  onEntryLongPress,
  onResourceDrop,
  showTravelSlivers,
  industryAccent,
}: DayStaffColumnsProps) {
  const grouped = groupByOperator(entries, operators);

  return (
    <div
      data-testid="day-staff-columns"
      style={{
        flex: 1,
        display: 'flex',
        flexDirection: 'row',
        gap: 12,
        padding: 16,
        overflowX: 'auto',
        backgroundColor: admin.bg,
      }}
    >
      {operators.length === 0 ? (
        <div style={{ flex: 1, textAlign: 'center', padding: '48px 0', color: '#8E8E93', fontSize: 14 }}>
          No operators on shift today
        </div>
      ) : (
        operators.map((op) => (
          <ResourceColumn
            key={op.id}
            operator={op}
            entries={grouped[op.id] ?? []}
            travelBufferMinutes={travelBufferMinutes}
            onEntryTap={onEntryTap}
            onEntryLongPress={onEntryLongPress}
            showTravelSlivers={showTravelSlivers}
            industryAccent={industryAccent}
            onResourceDrop={onResourceDrop}
            draggable={onResourceDrop != null}
          />
        ))
      )}
    </div>
  );
}
